import * as yup from 'yup';
import { EyeOff } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { CouponType, GetCouponQuery as GetCoupon, ProductType } from '__generated__/graphql';
import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import CouponValueInput from './CouponValueInput';
import DateRangeForm, { dateRangeObject } from '../../../Common/DateRangeForm';
import EditCouponMutation from './EditCouponMutation';
import GenerateCouponCodesForm from './GenerateCouponCodesForm';
import ProductSelectionForm from '../../../Common/ProductSelectionForm';
import TicketSelectionForm from '../../../Common/TicketSelectionForm';
import UI from '../../../../common/UI';
import useIntegerInput from '../../../../common/useIntegerInput';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useScroll from '../../../../common/useScroll';
import useValidation from '../../../../common/useValidation';

type Coupon = GetCoupon['coupon'];
type Event = GetCoupon['event'];

const couponFormSchema = yup.object().shape({
  prefix: yup.string().min(2).max(50).required(),
  value: yup.number().required().min(0),
  notes: yup.string().nullable(),
  ...dateRangeObject(false, false),
});

export interface EditCouponFormProps {
  coupon: Coupon;
  event: Event;
}

const EditCouponForm = ({ coupon, event }: EditCouponFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const notifier = useNotifier();
  const project = useProject();
  const integerInput = useIntegerInput();
  const { scrollToIfInvisible } = useScroll();

  const getValues = (coupon: Coupon) => ({
    id: coupon.id,
    prefix: coupon.prefix,
    type: coupon.type,
    value: coupon.value,
    start_date: coupon.start_date,
    end_date: coupon.end_date,
    max_uses: coupon.max_uses,
    notes: coupon.notes,
    tickets: coupon.tickets.map(({ id }) => ({ id })),
    products: coupon.products.map(({ id }) => ({ id })),
  });

  const form = useForm({
    values: getValues(coupon),
  });

  useNavigationPrompt(form.changed());

  const [editCoupon, { error, loading }] = useMutation(EditCouponMutation, {
    variables: {
      input: form.values,
    },
    onCompleted: ({ editCoupon: coupon }) => {
      notifier.success(t('notifications.coupon_saved'));

      form.reset(getValues(coupon));
    },
    onError: () => {
      scrollToIfInvisible(`CouponCard_${coupon.id}`);
    },
  });

  const { tickets } = event;
  const products = event.products
    .filter((product) => product.type === ProductType.item || product.type === ProductType.fee);

  const { valid, errors } = useValidation({
    schema: couponFormSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const setType = (type: CouponType) => {
    form.set((values) => {
      let { value } = values;

      if (value && type !== values.type) {
        if (type === CouponType.absolute) {
          value *= 100;
        } else if (type === CouponType.percentage) {
          value = Math.min(Math.round(value / 100), 100);
        }
      }

      return {
        ...values,
        type,
        value,
      };
    });
  };

  const [openGenerateCodesModal, closeGenerateCodesModal] = useModal(() => {
    const close = () => {
      closeGenerateCodesModal();
    };

    return (
      <UI.Modal maxWidth={400} close={close}>
        <GenerateCouponCodesForm
          coupon={coupon}
          onSuccess={close}
        />
      </UI.Modal>
    );
  }, [coupon]);

  return (
    <UI.Div id={`EditCouponForm_${coupon.id}`}>
      <UI.Form onSubmit={() => editCoupon()}>
        <UI.FormGrid>
          <UI.ServerErrors error={error} />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('basic_info')}
            </UI.Legend>
            <UI.InputDescription>
              {t('coupon_basic_info')}
            </UI.InputDescription>
          </UI.Div>

          <UI.InputGroup sc={{ valid: !errors.prefix, touched: form.touched.prefix }}>
            <UI.InputLabel htmlFor="CouponPrefix">
              {t('coupon_code')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('prefix')}
              placeholder={t('coupon_code_placeholder')}
              id="CouponPrefix"
            />
            <UI.ErrorMessages attribute={t('coupon_code')} errors={errors.prefix} />
          </UI.InputGroup>

          <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
            <UI.InputGroup sc={{ valid: !errors.type, touched: form.touched.type }}>
              <UI.InputLabel>
                {t('coupon_type')}
              </UI.InputLabel>
              <UI.Radio
                onChange={() => setType(CouponType.absolute)}
                checked={form.values.type === CouponType.absolute}
              >
                {t('absolute_amount')}
              </UI.Radio>
              <UI.Radio
                onChange={() => setType(CouponType.percentage)}
                checked={form.values.type === CouponType.percentage}
              >
                {t('percentage_amount')}
              </UI.Radio>
              <UI.ErrorMessages
                attribute={t('coupon_type')}
                errors={errors.type}
              />
            </UI.InputGroup>

            <UI.InputGroup sc={{ valid: !errors.value, touched: form.touched.value }}>
              <UI.InputLabel htmlFor="CouponValue">
                {t('coupon_value')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <CouponValueInput form={form} />
              <UI.ErrorMessages attribute={t('coupon_value')} errors={errors.value} />
            </UI.InputGroup>
          </UI.FormGrid>

          <UI.Div>
            <UI.InputLabel htmlFor="CouponNotes">
              {t('internal_notes')}
              <UI.Icon sc={{ ml: 1, muted: true }}>
                <EyeOff />
              </UI.Icon>
            </UI.InputLabel>
            <UI.FormGrid>
              <UI.TextArea
                {...form.textarea('notes')}
                sc={{ background: 'info.50', minHeight: 20 }}
                id="CouponNotes"
              />
            </UI.FormGrid>
          </UI.Div>

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('tickets_products')}
            </UI.Legend>
            <UI.InputDescription>
              {t('tickets_products_info')}
            </UI.InputDescription>
          </UI.Div>

          <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
            <TicketSelectionForm
              form={form}
              tickets={tickets}
              errors={errors}
              event={event}
            />

            <ProductSelectionForm
              form={form}
              products={products}
              errors={errors}
              event={event}
            />
          </UI.FormGrid>

          {coupon.active && form.values.tickets.length === 0 && form.values.products.length === 0 && (
            <UI.Warning>
              {(t('coupon_no_tickets_or_products_warning'))}
            </UI.Warning>
          )}

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('availability')}
            </UI.Legend>
            <UI.InputDescription>
              {t('coupon_start_and_end_date')}
            </UI.InputDescription>
          </UI.Div>

          <DateRangeForm form={form} errors={errors} />

          <UI.InputGroup sc={{ valid: !errors.max_uses, touched: form.touched.max_uses }}>
            <UI.InputLabel htmlFor="CouponMaxUses">{t('max_uses')}</UI.InputLabel>
            <UI.InputDescription>
              {t('coupon_max_uses_description')}
            </UI.InputDescription>
            <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
              <UI.Div>
                <UI.DebouncedInput
                  {...form.text('max_uses', { inputHandler: integerInput() })}
                  maxLength={10}
                  inputMode="numeric"
                  format={integerInput().format}
                  match={/[\d]/g}
                  placeholder="∞"
                  id="CouponMaxUses"
                />
              </UI.Div>
            </UI.FormGrid>
            <UI.ErrorMessages attribute={t('max_uses')} errors={errors.max_uses} />
          </UI.InputGroup>

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('generate_coupon_codes')}
            </UI.Legend>
            <UI.InputDescription>
              {t('generate_coupon_codes_description')}
            </UI.InputDescription>
          </UI.Div>

          <UI.Div>
            {coupon.codes_count > 0 && (
              <>
                <UI.Div sc={{ mb: 2 }}>
                  <UI.Label sc={{ strong: true }}>
                    {coupon.example.value}
                  </UI.Label>
                  {coupon.codes_count > 1 && (` ${t('and_n_more', {
                    n: formatNumber(coupon.codes_count - 1), count: coupon.codes_count - 1,
                  })}`)}
                </UI.Div>
                <UI.AButton
                  href={coupon.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('download_coupon_codes')}
                </UI.AButton>
                {' '}
                <UI.Button onClick={() => openGenerateCodesModal()}>
                  {t('generate')}
                </UI.Button>
              </>
            )}

            {coupon.codes_count === 0 && (
              <UI.Button onClick={() => openGenerateCodesModal()}>
                {t('generate')}
              </UI.Button>
            )}
          </UI.Div>

          <UI.HR />

          <UI.FormButtons
            form={form}
            submitLabel={t('save_coupon')}
            closeTo={`/${project.id}/events/${event.id}/coupons`}
            loading={loading}
            valid={valid}
          />
        </UI.FormGrid>
      </UI.Form>
    </UI.Div>
  );
};

export default EditCouponForm;
